'use client'

import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'

interface HeatmapCoin {
  id: string
  name: string
  symbol: string
  image: string
  price: number
  priceFormatted: string
  change24h: number
  change7d: number
  volume: number
  marketCap: number
  marketCapRank: number
  sparkline: number[]
  category: string
}

interface TileRect {
  x: number; y: number; w: number; h: number
}

interface CoinTile extends TileRect {
  coin: HeatmapCoin
}

interface CategoryBlock extends TileRect {
  category: string
  coins: HeatmapCoin[]
  totalMcap: number
}

// ─── Squarified treemap ──────────────────────────────────────

function squarify<T extends { value: number }>(
  items: T[],
  x: number, y: number, w: number, h: number,
): (T & TileRect)[] {
  if (items.length === 0) return []
  if (items.length === 1) return [{ ...items[0], x, y, w, h }]

  const total = items.reduce((s, i) => s + i.value, 0)
  if (total === 0) return []

  let half = total / 2
  let sum = 0
  let splitIdx = 0
  for (let i = 0; i < items.length - 1; i++) {
    sum += items[i].value
    if (sum >= half) { splitIdx = i + 1; break }
  }
  if (splitIdx === 0) splitIdx = 1

  const left = items.slice(0, splitIdx)
  const right = items.slice(splitIdx)
  const leftSum = left.reduce((s, i) => s + i.value, 0)
  const ratio = leftSum / total

  if (w >= h) {
    const lw = w * ratio
    return [...squarify(left, x, y, lw, h), ...squarify(right, x + lw, y, w - lw, h)]
  } else {
    const lh = h * ratio
    return [...squarify(left, x, y, w, lh), ...squarify(right, x, y + lh, w, h - lh)]
  }
}

// ─── Colors ──────────────────────────────────────────────────

function getColor(change: number): string {
  if (Math.abs(change) < 0.5) return 'rgba(63,63,70,0.5)'
  const i = Math.min(Math.abs(change) / 10, 1)
  if (change > 0) {
    return `rgba(${Math.round(10 + 24 * i)},${Math.round(40 + 157 * i)},${Math.round(20 + 74 * i)},${(0.25 + i * 0.5).toFixed(2)})`
  }
  return `rgba(${Math.round(60 + 179 * i)},${Math.round(20 + 48 * i)},${Math.round(20 + 48 * i)},${(0.25 + i * 0.5).toFixed(2)})`
}

function getTextColor(c: number): string {
  if (Math.abs(c) < 0.5) return '#a1a1aa'
  return c > 0 ? '#4ade80' : '#f87171'
}

function getBorderColor(c: number): string {
  if (Math.abs(c) < 0.5) return 'rgba(63,63,70,0.2)'
  const i = Math.min(Math.abs(c) / 10, 1)
  return c > 0 ? `rgba(34,197,94,${0.08 + i * 0.15})` : `rgba(239,68,68,${0.08 + i * 0.15})`
}

function fmtVol(v: number): string {
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`
  return `$${(v / 1e3).toFixed(0)}K`
}

// ─── Mini sparkline ──────────────────────────────────────────

function MiniSparkline({ prices, positive, w = 60, h = 20 }: { prices: number[]; positive: boolean; w?: number; h?: number }) {
  if (!prices || prices.length < 4) return null
  const step = Math.max(1, Math.floor(prices.length / 16))
  const sampled = prices.filter((_, i) => i % step === 0)
  if (sampled.length < 2) return null

  const min = Math.min(...sampled)
  const max = Math.max(...sampled)
  const range = max - min || 1

  const points = sampled
    .map((p, i) => `${((i / (sampled.length - 1)) * w).toFixed(1)},${(1 + (1 - (p - min) / range) * (h - 2)).toFixed(1)}`)
    .join(' ')

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block">
      <polyline points={points} fill="none" stroke={positive ? '#22c55e' : '#ef4444'} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />
    </svg>
  )
}

// ─── Category label color ─────────────────────────────────────

const CAT_LABEL_COLOR = 'rgba(161,161,170,0.45)'

// ─── Constants ───────────────────────────────────────────────

const DEFAULT_W = 1200
const DEFAULT_H = 720
const CONTROLS_H = 48
const CAT_LABEL_H = 22
const CAT_PAD = 4

// ─── Component ───────────────────────────────────────────────

export default function HeatmapClient({ coins, btcDominance }: { coins: HeatmapCoin[]; btcDominance: number }) {
  const [timeframe, setTimeframe] = useState<'24h' | '7d'>('24h')
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 })
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [viewW, setViewW] = useState(DEFAULT_W)
  const [viewH, setViewH] = useState(DEFAULT_H)
  const containerRef = useRef<HTMLDivElement>(null)
  const fullscreenRef = useRef<HTMLDivElement>(null)

  // Resize for fullscreen
  useEffect(() => {
    if (isFullscreen) {
      setViewW(window.innerWidth)
      setViewH(window.innerHeight - CONTROLS_H)
    } else {
      setViewW(DEFAULT_W)
      setViewH(DEFAULT_H)
    }
  }, [isFullscreen])

  useEffect(() => {
    if (!isFullscreen) return
    const fn = () => { setViewW(window.innerWidth); setViewH(window.innerHeight - CONTROLS_H) }
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, [isFullscreen])

  // Pulse: top 3 gainers + top 3 losers by current timeframe
  const pulseIds = useMemo(() => {
    const key = timeframe === '24h' ? 'change24h' : 'change7d'
    const sorted = [...coins].sort((a, b) => b[key] - a[key])
    const gainers = sorted.filter(c => c[key] > 0).slice(0, 3).map(c => c.id)
    const losers = sorted.filter(c => c[key] < 0).slice(-3).map(c => c.id)
    return { gainers: new Set(gainers), losers: new Set(losers) }
  }, [coins, timeframe])

  // Top 5 overall by market cap (for rich tile display)
  const top5Ids = useMemo(() => {
    return new Set([...coins].sort((a, b) => b.marketCap - a.marketCap).slice(0, 5).map(c => c.id))
  }, [coins])

  // Group coins by category
  const categoryGroups = useMemo(() => {
    const groups: Record<string, HeatmapCoin[]> = {}
    for (const coin of coins) {
      const cat = coin.category
      if (!groups[cat]) groups[cat] = []
      groups[cat].push(coin)
    }
    // Sort groups by total market cap descending
    return Object.entries(groups)
      .map(([category, catCoins]) => ({
        category,
        coins: catCoins.sort((a, b) => b.marketCap - a.marketCap),
        totalMcap: catCoins.reduce((s, c) => s + c.marketCap, 0),
      }))
      .sort((a, b) => b.totalMcap - a.totalMcap)
  }, [coins])

  // Layout: outer treemap for categories, inner treemap for coins
  const layout = useMemo(() => {
    // Outer treemap
    const catItems = categoryGroups.map(g => ({
      ...g,
      value: Math.sqrt(g.totalMcap),
    }))
    const catRects = squarify(catItems, 0, 0, viewW, viewH)

    // Inner treemaps
    const allTiles: { coin: HeatmapCoin; x: number; y: number; w: number; h: number; catRect: TileRect }[] = []

    for (const cr of catRects) {
      const innerX = cr.x + CAT_PAD
      const innerY = cr.y + CAT_LABEL_H + CAT_PAD
      const innerW = Math.max(0, cr.w - CAT_PAD * 2)
      const innerH = Math.max(0, cr.h - CAT_LABEL_H - CAT_PAD * 2)

      if (innerW < 10 || innerH < 10) continue

      const coinItems = cr.coins.map(coin => ({
        coin,
        value: Math.sqrt(coin.marketCap),
      }))

      const catRect = { x: cr.x, y: cr.y, w: cr.w, h: cr.h }
      const totalVal = coinItems.reduce((s, c) => s + c.value, 0)
      const firstRatio = totalVal > 0 ? coinItems[0].value / totalVal : 0

      // Only force top-slice when #1 coin dominates (>40% of category)
      if (coinItems.length >= 2 && innerH > 60 && firstRatio > 0.4) {
        const first = coinItems[0]
        const rest = coinItems.slice(1)
        // Cap at 50% height so remaining coins aren't squished
        const cappedRatio = Math.min(firstRatio, 0.5)
        const firstH = innerH * cappedRatio

        allTiles.push({ ...first, x: innerX, y: innerY, w: innerW, h: firstH, catRect })

        const restY = innerY + firstH
        const restH = innerH - firstH
        if (restH > 5) {
          const restRects = squarify(rest, innerX, restY, innerW, restH)
          for (const ct of restRects) {
            allTiles.push({ ...ct, catRect })
          }
        }
      } else {
        // Normal squarify for evenly-distributed categories
        const coinRects = squarify(coinItems, innerX, innerY, innerW, innerH)
        for (const ct of coinRects) {
          allTiles.push({ ...ct, catRect })
        }
      }
    }

    return { catRects, allTiles }
  }, [categoryGroups, viewW, viewH])

  const hoveredCoin = hoveredId ? coins.find(c => c.id === hoveredId) : null

  function handleMouseMove(e: React.MouseEvent) {
    const t = containerRef.current
    if (!t) return
    const r = t.getBoundingClientRect()
    setTooltipPos({ x: e.clientX - r.left, y: e.clientY - r.top })
  }

  const toggleFullscreen = useCallback(async () => {
    if (!fullscreenRef.current) return
    if (!isFullscreen) {
      try { await fullscreenRef.current.requestFullscreen?.() } catch {}
      setIsFullscreen(true)
    } else {
      try { if (document.fullscreenElement) await document.exitFullscreen() } catch {}
      setIsFullscreen(false)
    }
  }, [isFullscreen])

  useEffect(() => {
    const fn = () => { if (!document.fullscreenElement) setIsFullscreen(false) }
    document.addEventListener('fullscreenchange', fn)
    return () => document.removeEventListener('fullscreenchange', fn)
  }, [])

  useEffect(() => {
    document.body.style.overflow = isFullscreen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isFullscreen])

  function renderMap() {
    return (
      <>
        <svg viewBox={`0 0 ${viewW} ${viewH}`} className="w-full h-full block" preserveAspectRatio="none">
          {/* Category backgrounds */}
          {layout.catRects.map((cr) => (
            <g key={cr.category}>
              <rect
                x={cr.x + 0.5} y={cr.y + 0.5}
                width={Math.max(0, cr.w - 1)} height={Math.max(0, cr.h - 1)}
                fill="rgba(0,0,0,0.3)"
                stroke="rgba(255,255,255,0.06)"
                strokeWidth="1"
                rx="3"
              />
              {cr.w > 60 && (
               <text
               x={cr.x + 6} y={cr.y + CAT_LABEL_H / 2 + 2}
               fill={CAT_LABEL_COLOR}
               fontSize="10" fontWeight="600" letterSpacing="1.5"
               fontFamily="var(--font-saira), 'Saira', sans-serif"
               dominantBaseline="middle"
               className="pointer-events-none select-none uppercase"
             >
               {cr.category}
             </text>
              )}
            </g>
          ))}

          {/* Coin tiles */}
          {layout.allTiles.map((tile) => {
            const change = timeframe === '24h' ? tile.coin.change24h : tile.coin.change7d
            const isHov = hoveredId === tile.coin.id
            const minDim = tile.w > tile.h * 1.8 ? Math.max(tile.w * 0.4, tile.h) : Math.min(tile.w, tile.h)

            const isTop5 = top5Ids.has(tile.coin.id)
            const isPulseGreen = pulseIds.gainers.has(tile.coin.id)
            const isPulseRed = pulseIds.losers.has(tile.coin.id)

            return (
              <g key={tile.coin.id}>
                {/* Pulse glow ring */}
                {(isPulseGreen || isPulseRed) && (
                  <rect
                    x={tile.x + 2} y={tile.y + 2}
                    width={Math.max(0, tile.w - 4)} height={Math.max(0, tile.h - 4)}
                    fill="none"
                    stroke={isPulseGreen ? 'rgba(34,197,94,0.5)' : 'rgba(239,68,68,0.5)'}
                    strokeWidth="2"
                    rx="2"
                    className="pointer-events-none"
                  >
                    <animate attributeName="opacity" values="0.3;1;0.3" dur="2s" repeatCount="indefinite" />
                  </rect>
                )}

                <rect
                  x={tile.x + 1} y={tile.y + 1}
                  width={Math.max(0, tile.w - 2)} height={Math.max(0, tile.h - 2)}
                  fill={getColor(change)}
                  stroke={isHov ? '#ffffff' : getBorderColor(change)}
                  strokeWidth={isHov ? 2 : 0.5}
                  rx="2"
                  className="cursor-pointer transition-opacity duration-150"
                  opacity={hoveredId && !isHov ? 0.4 : 1}
                  onMouseEnter={() => setHoveredId(tile.coin.id)}
                />

                {/* Symbol — always if tile big enough */}
                {minDim > 24 && (
                  <text
                    x={tile.x + tile.w / 2}
                    y={tile.y + (isTop5 && minDim > 70 ? tile.h * 0.3 : tile.h / 2 - (minDim > 50 ? 5 : 0))}
                    textAnchor="middle" dominantBaseline="central"
                    fill={isHov ? '#fff' : '#d4d4d8'}
                    fontSize={tile.coin.id === 'bitcoin' ? (tile.w > 500 ? 72 : 32) : tile.coin.id === 'ethereum' ? (tile.w > 400 ? 44 : 22) : minDim > 200 ? 32 : minDim > 150 ? 24 : minDim > 100 ? 16 : minDim > 70 ? 13 : minDim > 45 ? 10 : 8}                    fontWeight="700"
                    fontFamily="var(--font-saira), 'Saira', sans-serif"
                    className="pointer-events-none select-none"
                  >
                    {tile.coin.symbol}
                  </text>
                )}

                {/* Price — top 5 only, if space */}
                {isTop5 && minDim > 70 && (
                  <text
                    x={tile.x + tile.w / 2}
                    y={tile.y + tile.h * 0.48}
                    textAnchor="middle" dominantBaseline="central"
                    fill="#a1a1aa"
                    fontSize={tile.coin.id === 'bitcoin' ? (tile.w > 500 ? 36 : 16) : tile.coin.id === 'ethereum' ? (tile.w > 400 ? 22 : 12) : minDim > 200 ? 22 : minDim > 150 ? 16 : minDim > 100 ? 10 : 8}

                    fontWeight="500"
                    fontFamily="var(--font-saira), 'Saira', sans-serif"
                    className="pointer-events-none select-none"
                  >
                    {tile.coin.priceFormatted}
                  </text>
                )}

                {/* Change % */}
                {minDim > 36 && (
                  <text
                    x={tile.x + tile.w / 2}
                    y={tile.y + (isTop5 && minDim > 70 ? tile.h * 0.64 : tile.h / 2 + (minDim > 70 ? 12 : 8))}
                    textAnchor="middle" dominantBaseline="central"
                    fill={getTextColor(change)}
                    fontSize={tile.coin.id === 'bitcoin' ? (tile.w > 500 ? 30 : 14) : tile.coin.id === 'ethereum' ? (tile.w > 400 ? 18 : 11) : minDim > 200 ? 20 : minDim > 150 ? 16 : minDim > 100 ? 12 : minDim > 70 ? 10 : 8}
                    fontWeight={(isPulseGreen || isPulseRed) ? '800' : '600'}
                    fontFamily="var(--font-saira), 'Saira', sans-serif"
                    className="pointer-events-none select-none"
                  >
                    {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                  </text>
                )}

                {/* BTC dominance */}
                {tile.coin.id === 'bitcoin' && minDim > 90 && (
                  <text
                    x={tile.x + tile.w / 2}
                    y={tile.y + tile.h * 0.8}
                    textAnchor="middle" dominantBaseline="central"
                    fill="rgba(212,212,216,0.7)"
                    fontSize={minDim > 200 ? 12 : 8} fontWeight="500"
                    fontFamily="var(--font-saira), 'Saira', sans-serif"
                    className="pointer-events-none select-none"
                  >
                 {btcDominance.toFixed(1)}% dominance
                </text>
                )}
              </g>
            )
          })}
        </svg>

        {/* Click overlay */}
        <div className="absolute inset-0">
          {layout.allTiles.map((tile) => (
           <Link
           key={tile.coin.id}
           href={`/prices/${tile.coin.id}`}
           className="absolute block"
           aria-label={`View ${tile.coin.name} (${tile.coin.symbol}) price`}
              style={{
                left: `${(tile.x / viewW) * 100}%`,
                top: `${(tile.y / viewH) * 100}%`,
                width: `${(tile.w / viewW) * 100}%`,
                height: `${(tile.h / viewH) * 100}%`,
              }}
              onMouseEnter={() => setHoveredId(tile.coin.id)}
            />
          ))}
        </div>

        {/* Tooltip */}
        {hoveredCoin && (
          <div
            className="absolute z-50 pointer-events-none"
            style={{
              left: tooltipPos.x + 16,
              top: tooltipPos.y - 10,
              transform: tooltipPos.x > (containerRef.current?.clientWidth ?? 800) * 0.65
                ? 'translateX(-110%)' : 'none',
            }}
          >
            <div className="bg-zinc-950 border border-zinc-700 px-4 py-3 shadow-2xl min-w-[200px]">
              <div className="flex items-center gap-2 mb-2">
                {hoveredCoin.image && (
                  <Image src={hoveredCoin.image} alt={hoveredCoin.name} width={20} height={20} className="rounded-full" />
                )}
                <span className="text-white text-sm font-semibold">{hoveredCoin.name}</span>
                <span className="text-zinc-400 text-xs">{hoveredCoin.symbol}</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-zinc-400 text-xs">Price</span>
                  <span className="text-white text-xs font-semibold tabular-nums">{hoveredCoin.priceFormatted}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400 text-xs">24h</span>
                  <span className={`text-xs font-semibold tabular-nums ${hoveredCoin.change24h >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {hoveredCoin.change24h >= 0 ? '+' : ''}{hoveredCoin.change24h.toFixed(2)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400 text-xs">7d</span>
                  <span className={`text-xs font-semibold tabular-nums ${hoveredCoin.change7d >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {hoveredCoin.change7d >= 0 ? '+' : ''}{hoveredCoin.change7d.toFixed(2)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400 text-xs">Volume</span>
                  <span className="text-zinc-300 text-xs tabular-nums">{fmtVol(hoveredCoin.volume)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400 text-xs">Category</span>
                  <span className="text-zinc-300 text-xs">{hoveredCoin.category}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-zinc-400 text-xs">7d trend</span>
                  <MiniSparkline prices={hoveredCoin.sparkline} positive={hoveredCoin.change7d >= 0} />
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  return (
    <div ref={fullscreenRef} className={isFullscreen ? 'fixed inset-0 z-50 bg-black flex flex-col' : ''}>
      {/* Controls */}
      <div className={`flex items-center gap-1 ${isFullscreen ? 'px-4 py-3 flex-shrink-0' : 'mb-4'}`}
           style={isFullscreen ? { height: CONTROLS_H } : undefined}>
        {(['24h', '7d'] as const).map((tf) => (
          <button
            key={tf}
            onClick={() => setTimeframe(tf)}
            className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
              timeframe === tf ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white'
            }`}
            style={{ fontFamily: "var(--font-saira), 'Saira', sans-serif" }}
          >
            {tf}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1.5">
            <div className="w-3 h-3 bg-green-500/40" />
            <span className="text-zinc-400 text-[10px]">Gainer</span>
          </div>
          <div className="hidden sm:flex items-center gap-1.5">
            <div className="w-3 h-3 bg-red-500/40" />
            <span className="text-zinc-400 text-[10px]">Loser</span>
          </div>
          <div className="hidden sm:flex items-center gap-1.5">
            <div className="w-3 h-3 bg-zinc-700/60" />
            <span className="text-zinc-400 text-[10px]">Flat</span>
          </div>
          <button
            onClick={toggleFullscreen}
            className="ml-2 text-zinc-400 hover:text-white transition-colors p-1.5 border border-zinc-800 hover:border-zinc-600"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 14h6v6" /><path d="M20 10h-6V4" /><path d="M14 10l7-7" /><path d="M3 21l7-7" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h6v6" /><path d="M9 21H3v-6" /><path d="M21 3l-7 7" /><path d="M3 21l7-7" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Treemap */}
      <div
        ref={containerRef}
        className={`relative border border-zinc-800 overflow-hidden ${isFullscreen ? 'flex-1' : 'w-full'}`}
        style={isFullscreen ? undefined : { aspectRatio: `${DEFAULT_W} / ${DEFAULT_H}` }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredId(null)}
      >
        {renderMap()}
      </div>
    </div>
  )
}